import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SbomView from './SbomView';
import axios from 'axios';
import RepositoriesView from './RepositoryView';
import config from '../config/config';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

Modal.setAppElement('#root');

const sourceBadgeConfig = {
  upload: { icon: '⬆️', className: 'text-bg-secondary' },
  repository: { icon: '🌿', className: 'text-bg-primary' },
  'docker-image': { icon: '🐳', className: 'text-bg-info' },
  'helm-chart': { icon: '☸️', className: 'text-bg-success' },
  terraform: { icon: '🏗️', className: 'text-bg-warning' },
};

const SbomList = ({ repoName, onSbomsLoaded, onMalwareScan }) => {
  const { t } = useTranslation();
  const [sbomModalIsOpen, setSbomModalIsOpen] = useState(false);
  const [repositoriesModalIsOpen, setRepositoriesModalIsOpen] = useState(false);
  const [currentSbomLink, setCurrentSbomLink] = useState(null);
  const [currentSbomId, setCurrentSbomId] = useState(null);
  const [sboms, setSboms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [copiedHash, setCopiedHash] = useState('');

  const fetchSboms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.BASE_URL}/sbom/${repoName}/list`);
      const data = response.data || [];
      setSboms(data);
      if (onSbomsLoaded) onSbomsLoaded(data);
    } catch (error) {
      setErrorMessage('Failed to fetch SBOMs.');
      console.error('Failed to fetch sboms:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSboms();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoName]);

  const handleViewSbom = (sbomLink) => {
    setCurrentSbomLink(sbomLink);
    setSbomModalIsOpen(true);
  };

  const handleViewRepositories = (sbomId, sbomLink) => {
    setCurrentSbomId(sbomId);
    setCurrentSbomLink(sbomLink);
    setRepositoriesModalIsOpen(true);
  };

  const handleCreateSbom = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      await axios.post(`${config.BASE_URL}/sbom/${repoName}`);
      await fetchSboms();
    } catch (error) {
      setErrorMessage('Failed to create SBOM: ' + (error.response?.data?.error || error.message));
      console.error('Failed to create sbom:', error);
    }
    setLoading(false);
  };

  const handleDeleteSbom = async (commitHash) => {
    if (!window.confirm(`Delete SBOM for commit ${commitHash}?`)) return;
    setErrorMessage('');
    try {
      await axios.delete(`${config.BASE_URL}/sbom/${repoName}/${commitHash}-sbom.json`);
      setSboms(prev => prev.filter(s => s.CommitHash !== commitHash));
    } catch (error) {
      setErrorMessage('Failed to delete SBOM: ' + (error.response?.data?.error || error.message));
      console.error('Failed to delete sbom:', error);
    }
  };

  const handleCopyHash = (hash) => {
    navigator.clipboard.writeText(hash).then(() => {
      setCopiedHash(hash);
      setTimeout(() => setCopiedHash(''), 2000);
    });
  };

  const getSourceBadge = (sourceType) => {
    const normalizedType = sourceType || 'repository';
    const config = sourceBadgeConfig[normalizedType] || sourceBadgeConfig.repository;

    return {
      ...config,
      label: t(`sbomList.sourceTypes.${normalizedType}`),
    };
  };

  const getCommitHash = (sbom) => sbom.commitHash || sbom.CommitHash;
  const getSbomLink = (sbom) => sbom.sbomLink || sbom.SbomLink;

  return (
    <>
      <Modal
        isOpen={sbomModalIsOpen}
        onRequestClose={() => setSbomModalIsOpen(false)}
        contentLabel="SBOM View"
        style={{ overlay: { zIndex: 2000 } }}
      >
        <button onClick={() => setSbomModalIsOpen(false)} className="btn btn-success mb-2">Close</button>
        {currentSbomLink && <SbomView sbomLink={currentSbomLink} />}
      </Modal>

      <Modal
        isOpen={repositoriesModalIsOpen}
        onRequestClose={() => setRepositoriesModalIsOpen(false)}
        contentLabel="Repositories View"
        style={{ overlay: { zIndex: 2000 } }}
      >
        <button onClick={() => setRepositoriesModalIsOpen(false)} className="btn btn-success mb-2">Close</button>
        {currentSbomId && <RepositoriesView repoName={repoName} sbomId={currentSbomId} />}
      </Modal>

      {errorMessage && (
        <div className="alert alert-danger" role="alert">{errorMessage}</div>
      )}

      {/* Header */}
      <div className="ce-card-header" style={{ borderRadius: 0, borderLeft: 0, borderRight: 0, borderTop: 0 }}>
        <div>
          <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>
            {sboms.length} SBOM{sboms.length !== 1 ? 's' : ''}
          </span>
          {sboms.length > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginLeft: '0.5rem' }}>
              — latest: {sboms[0]?.CommitHash?.substring(0, 8)}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="ce-icon-btn" onClick={fetchSboms} disabled={loading} title="Refresh">↻</button>
          <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={handleCreateSbom} disabled={loading}>
            {loading ? '⏳ Working...' : '+ Generate SBOM'}
          </button>
          <Link to="/upload" className="ce-btn ce-btn--secondary ce-btn--sm">⬆️ Upload</Link>
        </div>
      </div>

      {loading && !sboms.length && (
        <div style={{ padding: '1rem' }}>
          <Skeleton.Table rows={4} cols={3} />
        </div>
      )}

      {!loading && sboms.length === 0 && !errorMessage && (
        <EmptyState
          icon="📦"
          title={t('sbomList.emptyTitle')}
          description={t('sbomList.emptyDescription')}
          action={{ label: t('sbomList.emptyAction'), onClick: handleCreateSbom }}
          size="sm"
        />
      )}

      {/* SBOM list */}
      {sboms.map((sbom, i) => {
        const commitHash = getCommitHash(sbom);
        const sbomLink = getSbomLink(sbom);
        const sourceType = sbom.sourceType || sbom.SourceType;
        const sourceValue = sbom.sourceValue || sbom.SourceValue;
        const sourceBadge = getSourceBadge(sourceType);

        return (
        <div key={commitHash} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.875rem',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--bs-border-color)',
          background: i === 0 ? 'var(--accent-light)' : 'transparent',
          transition: 'background 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bs-tertiary-bg)'}
        onMouseLeave={e => e.currentTarget.style.background = i === 0 ? 'var(--accent-light)' : 'transparent'}
        >
          {i === 0 && (
            <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 100, background: 'var(--accent)', color: '#fff', whiteSpace: 'nowrap' }}>
              LATEST
            </span>
          )}
          <code style={{ fontSize: '0.8125rem', fontFamily: 'var(--font-mono)', color: 'var(--bs-secondary-color)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {commitHash}
          </code>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <span className={`badge ${sourceBadge.className}`}>{sourceBadge.icon} {sourceBadge.label}</span>
              {sourceValue ? (
                <span
                  className="badge text-bg-light border"
                  title={sourceValue}
                  style={{ maxWidth: '24rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {t('sbomList.sourceLabel')}: {sourceValue}
                </span>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
            <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => handleViewSbom(sbomLink)} title="View SBOM">👁 View</button>
            <a href={sbomLink} download className="ce-btn ce-btn--secondary ce-btn--sm" title="Download">⬇ Download</a>
            <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => handleViewRepositories(commitHash, sbomLink)} title="View Dependency Repositories">🔗 Deps</button>
            {onMalwareScan && (
              <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => onMalwareScan(commitHash)} title="Scan for malware & typosquatting">🦠</button>
            )}
            <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => handleCopyHash(commitHash)} title="Copy hash">
              {copiedHash === commitHash ? '✓' : '⧉'}
            </button>
            <button className="ce-btn ce-btn--danger ce-btn--sm" onClick={() => handleDeleteSbom(commitHash)} title="Delete">🗑</button>
          </div>
        </div>
        );
      })}
    </>
  );
};

export default SbomList;
